<template>
  <div class="modal-card px-15">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <span class="modal-card-title is-size-5 has-text-centered">{{
        General.MODELS_EXISTED
      }}</span>
      <div
        class="close-button cursor-pointer"
        role="button"
        :aria-label="General.CLOSE"
        @click.stop="close"
      >
        <b-icon icon="close" :type="isContrast ? 'is-white-dark' : ''" />
      </div>
    </header>
    <div class="columns mt-10">
      <b-field class="column is-8 is-9-desktop">
        <b-input
          v-model="modelSearch"
          :placeholder="General.PLACEHOLDER.TYPE_TO_SEARCH"
          type="search"
          icon="magnify"
          iconClickable
        />
      </b-field>
      <div class="column is-4 is-3-desktop">
        <b-field
          :label="General.DOCUMENT_TYPE"
          labelPosition="on-border"
          class="select-field"
        >
          <b-select
            v-model="typeSearch"
            :disabled="Boolean(defaultDocumentType)"
            :placeholder="General.SELECT_TYPE"
            expanded
          >
            <option :value="{}" aria-role="listitem">{{ General.ALL }}</option>
            <option
              v-for="option in getValidFileTypes"
              :key="option.id"
              :value="option"
            >
              {{ option.type }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>
    <div class="list-model">
      <span
        class="has-text-weight-semibold has-text-color-7 bb-custom is-flex"
        >{{ General.NAME }}</span
      >
      <div v-if="modelList.length && !isLoading">
        <div
          v-for="model in modelList"
          :key="model.id"
          class="model-item bb-custom"
          @click="selectModel(model)"
        >
          <b-icon
            class="spacing-mr-10"
            :type="isContrast ? 'is-white' : 'is-primary'"
            icon="file-document"
            size="is-small"
          />
          <span>{{ model.name }}</span>
        </div>
      </div>
      <div v-else id="models-empty" class="has-text-color-7">
        {{ General.TEXTS.NO_MODELS }}
      </div>
    </div>
    <div class="mt-20 is-flex is-justify-content-flex-end">
      <b-button
        :type="isContrast ? 'is-white' : 'is-orange'"
        outlined
        :expanded="isMobile"
        @click="close"
      >
        {{ General.CANCEL }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import { App, General } from "../../utils/constants";

export default {
  name: "ModalModels",

  props: {
    processId: {
      type: String,
      default: ""
    },
    defaultDocumentType: {
      type: String,
      default: ""
    },
    onSelect: {
      type: Function,
      default: () => null
    },
    showOnlyPrimaryTypes: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      App,
      General,
      modelSearch: "",
      isLoading: false,
      typeSearch: {}
    };
  },

  computed: {
    ...mapGetters({
      getFileTypes: "getFileTypes",
      getAllModels: "getAllModels",
      getModelsByTypeFiltered: "getModelsByTypeFiltered",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    getValidFileTypes() {
      return this.getFileTypes.filter(type => type.layout_template);
    },

    modelList() {
      const regexForModel = new RegExp(this.modelSearch.trim(), "i");

      const currentModelList = this.defaultDocumentType
        ? this.getModelsByTypeFiltered("") || {}
        : this.getAllModels || {};

      return currentModelList.filter(model => {
        if (this.typeSearch?.id) {
          return (
            regexForModel.test(model.name) &&
            model.document_type === this.typeSearch.id
          );
        }
        return (
          regexForModel.test(model.name) &&
          this.getValidFileTypes.some(type => type.id == model.document_type)
        );
      });
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await this.$store.dispatch("fileTypesRequest", {
      state: true,
      excludeRequestTypes: this.showOnlyPrimaryTypes
    });
    this.defaultDocumentType
      ? await this.getModelsListByTypeRequest({
          documentTypeId: this.defaultDocumentType
        })
      : await this.getAllModelList();
    this.setInitialType();
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      getModelsListByTypeRequest: "getModelsListByTypeRequest",
      copyGdocDocumentRequest: "copyGdocDocumentRequest",
      cloneGDocModelRequest: "cloneGDocModelRequest",
      getAllModelList: "getAllModelList"
    }),

    setInitialType() {
      if (this.getFileTypes.length && this.defaultDocumentType) {
        this.typeSearch = this.getFileTypes.find(fileType => {
          return fileType.id == this.defaultDocumentType;
        });
      }
    },

    async selectModel(model) {
      let finalModel = {};
      if (this.processId) {
        this.isLoading = true;
        const copiedModel = await this.copyGdocDocumentRequest({
          processId: this.processId,
          documentTypeId: model.document_type,
          documentModelId: model.id
        });
        finalModel = { gdocs_model_id: copiedModel.data.id };
        this.isLoading = false;
      } else {
        this.isLoading = true;
        const cloneGDocId = await this.cloneGDocModelRequest(model.id);
        this.isLoading = false;
        if (cloneGDocId)
          finalModel = {
            gdocs_model_id: cloneGDocId,
            all_parts: model.all_parts,
            parts: model.parts,
            document_type: model.document_type,
            name: model.name
          };
      }
      this.onSelect(finalModel);
      this.close();
    },

    close() {
      this.$parent.close();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  padding: 15px;
  background-color: $color-1 !important;

  @media screen and (max-width: $tablet) {
    width: auto !important;
    max-width: none;
    max-height: none !important;
  }
}

.list-model {
  overflow: auto;

  #models-empty {
    text-align: center;
    margin: 10px 0;
  }
}

.model-item {
  display: flex;
  align-items: center;
  padding: 5px;
  margin-top: 5px;
  color: $color-7;
  cursor: pointer;
  &:hover {
    background-color: $color-2;
  }
}

.bb-custom {
  border-bottom: 1px solid $grey-lighter;
}
</style>
